/**
 * Store — IndexedDB wrapper for persistence
 * Per PLAN.md storage/Store.js
 */

import { get, set, del, keys, entries, clear } from 'idb-keyval';
import { CanvasDocument } from '../models/CanvasDocument.js';
import { STORAGE_KEYS } from '../utils/constants.js';

export class Store {
    /**
     * Save a document
     * @param {CanvasDocument} document 
     */
    async saveDocument(document) {
        const data = document.toJSON();
        await set(`doc_${document.id}`, data);

        // Update document list
        const docList = await this.getDocumentList();
        const existing = docList.find(d => d.id === document.id);

        if (existing) {
            existing.name = document.name;
            existing.modifiedAt = document.modifiedAt;
        } else {
            docList.push({
                id: document.id,
                name: document.name,
                createdAt: document.createdAt,
                modifiedAt: document.modifiedAt
            });
        }

        await set(STORAGE_KEYS.DOCUMENTS, docList);
    }

    /**
     * Load a document by ID
     * @param {string} id 
     * @returns {Promise<CanvasDocument|null>}
     */
    async loadDocument(id) {
        const data = await get(`doc_${id}`);
        if (!data) return null;
        return CanvasDocument.fromJSON(data);
    }

    /**
     * Delete a document
     * @param {string} id 
     */
    async deleteDocument(id) {
        await del(`doc_${id}`);

        // Update document list
        const docList = await this.getDocumentList();
        const index = docList.findIndex(d => d.id === id);
        if (index > -1) {
            docList.splice(index, 1);
            await set(STORAGE_KEYS.DOCUMENTS, docList);
        }
    }

    /**
     * Get list of all documents
     * @returns {Promise<Array<{id: string, name: string, createdAt: number, modifiedAt: number}>>}
     */
    async getDocumentList() {
        const list = await get(STORAGE_KEYS.DOCUMENTS);
        return list || [];
    }

    /**
     * Get/set current document ID
     * @param {string} [id] - If provided, sets the current doc ID
     * @returns {Promise<string|null>}
     */
    async currentDocument(id) {
        if (id !== undefined) {
            await set(STORAGE_KEYS.CURRENT_DOC, id);
            return id;
        }
        return await get(STORAGE_KEYS.CURRENT_DOC) || null;
    }

    /**
     * Get/set theme preference
     * @param {string} [theme] 
     * @returns {Promise<string>}
     */
    async theme(theme) {
        if (theme !== undefined) {
            await set(STORAGE_KEYS.THEME, theme);
            return theme;
        }
        return await get(STORAGE_KEYS.THEME) || 'light';
    }

    /**
     * Clear all storage
     */
    async clearAll() {
        await clear();
    }
}
